import { websiteError as localWebsiteError } from '../lib/businessWebsite.js';
import { TextField } from './ui.js';

/**
 * The two things every new account gives - D-16 rulings 1 and 2.
 *
 * **One component, used by both account-creation paths.** Phase 0 found exactly
 * two doors into this portal - public sign-up, and the operator's invite
 * completed at set-password - and the whole interim tax position depends on
 * neither of them being able to make an account that skips these. Two copies of
 * this form would be two places for the wording to drift, and the wording is
 * what ruling 1 stores.
 */

/**
 * The exact sentence, and **it is the version string too.**
 *
 * `server/src/modules/agencies/business.ts` holds the original and stores it on
 * the row; `businessFields.test.tsx` reads that file off disk and fails if the
 * two differ. A tickbox whose label has drifted from what was recorded is a
 * consent record about a sentence nobody was shown.
 */
export const BUSINESS_CONFIRMATION =
  "EntityLinks is for businesses. I'm buying for my business, not for personal use, " +
  "and the website or profile I've given belongs to my business.";

export interface BusinessFieldsProps {
  website: string;
  onWebsiteChange: (value: string) => void;
  confirmed: boolean;
  onConfirmedChange: (value: boolean) => void;
  websiteError?: string | undefined;
  confirmedError?: string | undefined;
  disabled?: boolean;
}

export function BusinessFields({
  website,
  onWebsiteChange,
  confirmed,
  onConfirmedChange,
  websiteError,
  confirmedError,
  disabled = false,
}: BusinessFieldsProps): React.JSX.Element {
  return (
    <>
      <TextField
        label="Business website"
        /*
         * Ruling 2 names the alternative, and naming it matters: a consultant
         * with no site of their own is exactly the buyer who would otherwise
         * stop here, assume they are not eligible, and leave.
         */
        hint="Your website, or a LinkedIn or business profile if you don't have one."
        name="businessWebsite"
        autoComplete="url"
        placeholder="e.g. yourbusiness.com"
        value={website}
        onChange={(event) => onWebsiteChange(event.target.value)}
        /*
         * The server's message wins when it has spoken; otherwise the page
         * says the same thing as soon as the box loses its emptiness. Ruling C:
         * a buyer who typed `linkedin.com` and is told "must be a web address"
         * will type it again, because it is one.
         */
        error={websiteError ?? localWebsiteError(website)}
        disabled={disabled}
      />

      {/*
        **Above the button, and separate from the Terms line** (D-16 item 3).
        `SignupTermsLine` is a statement of what happens; this is a thing the
        buyer has to do. Running them together would make the Terms look like
        something being ticked, which ruling 9 of D-14 is explicit they are not.
      */}
      <div>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            name="businessConfirmed"
            checked={confirmed}
            onChange={(event) => onConfirmedChange(event.target.checked)}
            disabled={disabled}
            // WAIT-PL ruling 1: marked like every other field with an error,
            // so a refused submit can find it and move the cursor here.
            aria-invalid={confirmedError ? true : undefined}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-rule-strong text-fg focus:ring-blue-ink disabled:bg-sunken disabled:border-rule disabled:cursor-not-allowed"
          />
          <span className="text-sm text-fg">{BUSINESS_CONFIRMATION}</span>
        </label>
        {confirmedError ? (
          <p className="mt-1.5 text-sm text-danger">{confirmedError}</p>
        ) : null}
      </div>
    </>
  );
}
